import React from 'react';
import { Box, Button, EmptyState, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { LuMessageCircle } from 'react-icons/lu';
import { ExchangeOut, SessionOut } from '@/client';
import SessionRow from './SessionRow';
import { groupByRecency } from '@/utils/recencyGroups';
import RecencyGroupHeading from '@/components/common/RecencyGroupHeading';

interface SessionListProps {
	sessions: SessionOut[] | undefined;
	isLoading: boolean;
	isError: boolean;
	onRetry: () => void;
	houseId: number;
	onResume: (sessionId: string, exchanges: ExchangeOut[]) => void;
	onSelectCurrent: () => void;
	currentSessionId: string | null;
	currentSessionTitle: string;
	currentSessionCreatedAt: string | null;
	currentSessionIsStreaming: boolean;
}

/**
 * Renders the full list of past sessions, or appropriate empty/loading states.
 */
const SessionList: React.FC<SessionListProps> = ({
	sessions,
	isLoading,
	isError,
	onRetry,
	houseId,
	onResume,
	onSelectCurrent,
	currentSessionId,
	currentSessionTitle,
	currentSessionCreatedAt,
	currentSessionIsStreaming,
}) => {
	const optimisticCreatedAt = React.useMemo(() => {
		// The session ID is an intentional cache key: a new session needs a fresh fallback timestamp.
		void currentSessionId;
		return currentSessionCreatedAt ?? new Date().toISOString();
	}, [currentSessionCreatedAt, currentSessionId]);
	const displayedSessions = sessions ? [...sessions] : [];
	if (currentSessionId && !displayedSessions.some((session) => session.session_id === currentSessionId)) {
		displayedSessions.unshift({
			session_id: currentSessionId,
			created_at: optimisticCreatedAt,
			exchange_count: 1,
		});
	}

	if (isLoading && displayedSessions.length === 0) {
		return (
			<HStack justify="center" p={8}>
				<Spinner size="sm" />
				<Text textStyle="body.sm" color="fg.muted">
					Loading history…
				</Text>
			</HStack>
		);
	}

	if (isError && displayedSessions.length === 0) {
		return (
			<VStack gap={3} p={8} textAlign="center">
				<Text fontSize="sm" color="fg.muted">
					Unable to load chat history.
				</Text>
				<Button size="sm" variant="outline" borderRadius="full" onClick={onRetry}>
					Try again
				</Button>
			</VStack>
		);
	}

	if (displayedSessions.length === 0) {
		return (
			<EmptyState.Root size="md">
				<EmptyState.Content my={20}>
					<EmptyState.Indicator color="fg.muted">
						<LuMessageCircle />
					</EmptyState.Indicator>
					<EmptyState.Title color="fg.muted">No chats yet</EmptyState.Title>
				</EmptyState.Content>
			</EmptyState.Root>
		);
	}

	const groups = groupByRecency(displayedSessions, (session) => session.created_at);

	return (
		<VStack align="stretch" gap={0}>
			{groups.map((group) => (
				<Box key={group.label}>
					<RecencyGroupHeading label={group.label} />
					{group.items.map((session) => (
						<SessionRow
							key={session.session_id}
							session={session}
							houseId={houseId}
							onResume={onResume}
							onSelectCurrent={onSelectCurrent}
							isActive={session.session_id === currentSessionId}
							optimisticTitle={session.session_id === currentSessionId ? currentSessionTitle : undefined}
							isStreaming={session.session_id === currentSessionId && currentSessionIsStreaming}
						/>
					))}
				</Box>
			))}
		</VStack>
	);
};

export default SessionList;
